'use client';

import { c as _c } from "react/compiler-runtime";
import './slider-input.scss';
import { Range as RdxRange, Root as RdxRoot, Thumb as RdxThumb, Track as RdxTrack } from '@radix-ui/react-slider';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Either } from '@sola/schema/dotcom/helpers.ts';
import { trackClick } from '@sola/tracking/lib/analytics/events.ts';
import { cn } from '../../utils/helpers.ts';
import { Field, FieldProps } from '../field/field.tsx';
import { NumberField } from '../number-field/number-field.tsx';
interface SliderInputWithLabel {
  label: string;
}
interface SliderInputWithoutLabel {
  'aria-labelledby': React.ComponentPropsWithoutRef<'input'>['aria-labelledby'];
}
export type SliderInputProps = Partial<Either<SliderInputWithLabel, SliderInputWithoutLabel> & Omit<FieldProps, 'children' | 'required' | 'max' | 'min' | 'pattern' | 'valueAsNumber' | 'valueAsDate' | 'maxLength' | 'minLength' | 'validate' | 'value' | 'setValueAs' | 'deps' | 'isFocused'>> & {
  min: number;
  max: number;
  step?: number;
  value?: number[];
  label?: string;
  prefix?: string;
  name?: string;
  disabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
  sliderInputRef?: React.MutableRefObject<HTMLInputElement>;
  showThumbLabels?: boolean;
  showInputForm?: boolean;
  onCommit?: (sliderRange: {
    newValue: number[];
  }) => void;
};
export const SliderInput: React.FC<SliderInputProps> = t0 => {
  const $ = _c(51);
  let disabled;
  let fieldProps;
  let label;
  let max;
  let min;
  let onChange;
  let onCommit;
  let prefix;
  let showInputForm;
  let showThumbLabels;
  let sliderInputRef;
  let startingValue;
  let t1;
  let t2;
  if ($[0] !== t0) {
    const {
      "aria-labelledby": ariaLabelledBy,
      min: t3,
      max: t4,
      step: t5,
      value: t6,
      label: t7,
      prefix: t8,
      disabled: t9,
      orientation: t10,
      sliderInputRef: t11,
      onChange: t12,
      onCommit: t13,
      showThumbLabels: t14,
      showInputForm: t15,
      ...t16
    } = t0;
    min = t3;
    max = t4;
    t1 = t5;
    startingValue = t6;
    label = t7;
    prefix = t8;
    disabled = t9;
    t2 = t10;
    sliderInputRef = t11;
    onChange = t12;
    onCommit = t13;
    showThumbLabels = t14;
    showInputForm = t15;
    fieldProps = t16;
    $[0] = t0;
    $[1] = disabled;
    $[2] = fieldProps;
    $[3] = label;
    $[4] = max;
    $[5] = min;
    $[6] = onChange;
    $[7] = onCommit;
    $[8] = prefix;
    $[9] = showInputForm;
    $[10] = showThumbLabels;
    $[11] = sliderInputRef;
    $[12] = startingValue;
    $[13] = t1;
    $[14] = t2;
  } else {
    disabled = $[1];
    fieldProps = $[2];
    label = $[3];
    max = $[4];
    min = $[5];
    onChange = $[6];
    onCommit = $[7];
    prefix = $[8];
    showInputForm = $[9];
    showThumbLabels = $[10];
    sliderInputRef = $[11];
    startingValue = $[12];
    t1 = $[13];
    t2 = $[14];
  }
  const step = t1 === undefined ? 1 : t1;
  const orientation = t2 === undefined ? "horizontal" : t2;
  let t3;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["core"];
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const {
    t
  } = useTranslation(t3);
  let t4;
  if ($[16] !== max || $[17] !== min || $[18] !== startingValue) {
    t4 = startingValue || [min, max];
    $[16] = max;
    $[17] = min;
    $[18] = startingValue;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const [sliderValue, setSliderValue] = React.useState(t4);
  const [shouldCommit, setShouldCommit] = React.useState(false);
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const minInputRef = useRef(t5 as HTMLInputElement);
  let t6;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {};
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const maxInputRef = useRef(t6 as HTMLInputElement);
  const blurTimeoutRef = useRef(undefined);
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      blurTimeoutRef.current = setTimeout(() => {
        if (minInputRef.current) {
          minInputRef.current.blur();
        }
        if (maxInputRef.current) {
          maxInputRef.current.blur();
        }
      }, 2000);
    };
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handleInputChange = t7;
  let t8;
  let t9;
  if ($[23] !== startingValue) {
    t8 = () => {
      if (startingValue) {
        setSliderValue(startingValue);
      }
    };
    t9 = [startingValue];
    $[23] = startingValue;
    $[24] = t8;
    $[25] = t9;
  } else {
    t8 = $[24];
    t9 = $[25];
  }
  useEffect(t8, t9);
  const t10 = fieldProps as FieldProps;
  let t11;
  if ($[26] !== max || $[27] !== min || $[28] !== startingValue) {
    t11 = startingValue || [min, max];
    $[26] = max;
    $[27] = min;
    $[28] = startingValue;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== disabled || $[31] !== label || $[32] !== max || $[33] !== min || $[34] !== onChange || $[35] !== onCommit || $[36] !== orientation || $[37] !== prefix || $[38] !== shouldCommit || $[39] !== showInputForm || $[40] !== showThumbLabels || $[41] !== sliderValue || $[42] !== startingValue || $[43] !== step || $[44] !== t) {
    t12 = t13 => {
      const {
        controlProps,
        formMethods
      } = t13;
      const handleValueChange = value => {
        formMethods?.setValue(controlProps.name as string, value);
        setSliderValue(value);
        if (onChange) {
          onChange({
            newValue: value
          });
        }
        setShouldCommit(true);
      };
      const handleValueCommit = value_0 => {
        formMethods?.setValue(controlProps.name as string, value_0);
        setSliderValue(value_0);
        if (onCommit) {
          onCommit({
            newValue: value_0
          });
        }
        setShouldCommit(false);
      };
      return <>{label && <label className="form-label label" htmlFor={controlProps.id}>{label}</label>}<div className="Slider"><RdxRoot className={cn("Slider__root flex relative items-center", {
            "Slider__root--disabled": disabled,
            "flex-col": orientation === "vertical"
          })} defaultValue={startingValue} value={sliderValue} min={min} max={max} step={step} orientation={orientation} disabled={disabled} onClickCapture={() => {
            trackClick(`${label} - Slider`, undefined, "Slider__root");
          }} onValueChange={values => {
            const [newMin, newMax] = values;
            handleValueChange(newMax !== undefined ? [newMin, newMax] : [newMin]);
          }} onValueCommit={values_0 => {
            const [newMin_0, newMax_0] = values_0;
            handleValueCommit(newMax_0 !== undefined ? [newMin_0, newMax_0] : [newMin_0]);
          }} onPointerLeave={() => shouldCommit && handleValueCommit(sliderValue)}><RdxTrack className="Slider__track relative grow-1"><RdxRange className="Slider__range absolute" /></RdxTrack><RdxThumb className="Slider__thumb block">{showThumbLabels && <span className="Slider__thumb-label label label-moderate">{sliderValue?.[0]}</span>}</RdxThumb><RdxThumb className="Slider__thumb block">{showThumbLabels && <span className="Slider__thumb-label label label-moderate">{sliderValue?.[1]}</span>}</RdxThumb></RdxRoot>{showInputForm && <div className={cn("Slider__labels flex justify-between text-sm gap-comfy-md", {
            "mt-comfy-md": showThumbLabels,
            "mt-2": !showThumbLabels
          })}><div className="flex flex-col items-start gap-snug-xl mt-snug-lg"><span className="body label-md body-neutral">{t("core:slider.minimum")}</span><NumberField name={`${controlProps.name}_min`} inputRef={minInputRef} inputClasses="Slider__labels-input flex items-center justify-center label-moderate label-neutral" value={sliderValue?.[0]} onChange={handleInputChange} onBlur={e => {
                const newMin_1 = Math.min(Number(e.target.value), sliderValue[1]);
                handleValueCommit([newMin_1, sliderValue[1]]);
              }} aria-label={t("core:slider.minimum")} prefix={prefix} isCurrency={false} /></div><div className="flex flex-col items-end gap-snug-xl mt-snug-lg"><span className="body label-md body-neutral">{t("core:slider.maximum")}</span><NumberField name={`${controlProps.name}_max`} inputRef={maxInputRef} inputClasses="Slider__labels-input flex items-center justify-center label-moderate label-neutral" value={sliderValue?.[1]} onChange={handleInputChange} onBlur={e_0 => {
                const newMax_1 = Math.max(Number(e_0.target.value), sliderValue[0]);
                handleValueCommit([sliderValue[0], newMax_1]);
              }} aria-label={t("core:slider.maximum")} prefix={prefix} isCurrency={false} /></div></div>}</div></>;
    };
    $[30] = disabled;
    $[31] = label;
    $[32] = max;
    $[33] = min;
    $[34] = onChange;
    $[35] = onCommit;
    $[36] = orientation;
    $[37] = prefix;
    $[38] = shouldCommit;
    $[39] = showInputForm;
    $[40] = showThumbLabels;
    $[41] = sliderValue;
    $[42] = startingValue;
    $[43] = step;
    $[44] = t;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] !== sliderInputRef || $[47] !== t10 || $[48] !== t11 || $[49] !== t12) {
    t13 = <Field {...t10} value={t11} controlRef={sliderInputRef}>{t12}</Field>;
    $[46] = sliderInputRef;
    $[47] = t10;
    $[48] = t11;
    $[49] = t12;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  return t13;
};